import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import useCrmFinancialYear from '../../hooks/useCrmFinancialYear';
import CrmAttendanceSettings from './CrmAttendanceSettings';
import { useAuth } from '../../context/AuthContext';
import { downloadBlob, getApiErrorMessage, handleBlobDownloadResponse } from '../../utils/blobDownload';
import { Loader2, Mail, Building2, IndianRupee } from 'lucide-react';
import { isMobileContainer } from '../../utils/device';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from 'recharts';

const REPORTS = [
  { key: 'inquiries', label: 'Inquiries' },
  { key: 'joins', label: 'School Joins' },
  { key: 'salaries', label: 'Salaries' },
  { key: 'attendance', label: 'Attendance', superAdminOnly: true },
];

const EMPTY_MSG = 'No data available for the selected financial year.';

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <Card className="border-border">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value ?? '—'}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewTable({ title, columns, rows, rowKey }) {
  const isMobile = isMobileContainer();

  return (
    <Card className="border-border">
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className={isMobile ? '' : 'overflow-x-auto'}>
        {rows?.length > 0 ? (
          isMobile ? (
            <div className="space-y-3">
              {rows.map((row) => (
                <div key={rowKey(row)} className="border border-border rounded-lg p-4 space-y-2">
                  {columns.map((col) => (
                    <div key={col.key} className="flex justify-between gap-3 text-sm">
                      <span className="text-muted-foreground shrink-0">{col.label}</span>
                      <span className="text-foreground text-right font-medium">{col.render(row)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  {columns.map((col) => (
                    <th key={col.key} className="py-2 pr-4 font-medium">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={rowKey(row)} className="border-b border-border/50">
                    {columns.map((col) => (
                      <td key={col.key} className="py-2 pr-4">{col.render(row)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          <p className="text-muted-foreground text-center py-6">{EMPTY_MSG}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function CrmReports() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_slug === 'super_admin';
  const {
    financialYears,
    financialYearId,
    setFinancialYearId,
    loading: fyLoading,
    currentYear,
  } = useCrmFinancialYear();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!financialYearId) return;
    setSummaryLoading(true);
    try {
      const res = await api.get('/crm/reports/summary', {
        params: { financialYearId },
      });
      setSummary(res.data?.data || null);
    } catch (e) {
      setSummary(null);
      toast({
        title: 'Failed to load report summary',
        description: await getApiErrorMessage(e),
        variant: 'destructive',
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [financialYearId, toast]);

  useEffect(() => {
    if (financialYearId) {
      fetchSummary();
    }
  }, [financialYearId, fetchSummary]);

  const download = async (reportKey, format) => {
    const key = `${reportKey}-${format}`;
    setDownloading(key);
    try {
      const params = reportKey === 'attendance'
        ? { format, as_of: todayString() }
        : { format, financialYearId };
      const res = await api.get(`/crm/reports/${reportKey}`, {
        params,
        responseType: 'blob',
      });
      if (format === 'pdf') {
        const { filename } = await handleBlobDownloadResponse(
          res,
          'pdf',
          `crm-${reportKey}-${todayString()}.pdf`,
        );
        toast({ title: 'Downloaded', description: `Saved ${filename}` });
      } else {
        const filename = `crm-${reportKey}-${todayString()}.xlsx`;
        await downloadBlob(res.data, filename);
        toast({ title: 'Downloaded', description: `Saved ${filename}` });
      }
    } catch (e) {
      toast({
        title: 'Download failed',
        description: await getApiErrorMessage(e),
        variant: 'destructive',
      });
    } finally {
      setDownloading(null);
    }
  };

  const pageLoading = fyLoading || (summaryLoading && !summary);
  const inquiries = summary?.inquiries || {};
  const joins = summary?.joins || {};
  const salaries = summary?.salaries || {};
  const previews = summary?.previews || {};

  const inquiryChartData = (inquiries.monthly_series || []).map((item) => ({
    month: item.month,
    count: item.count,
  }));
  const joinChartData = (joins.monthly_series || []).map((item) => ({
    month: item.month,
    count: item.count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">CRM Reports</h1>
          <p className="text-muted-foreground">Export inquiries, joins, and salaries by financial year</p>
        </div>
        <div className="w-full sm:w-64 space-y-2">
          <Label>Financial Year</Label>
          <Select
            value={financialYearId || undefined}
            onValueChange={setFinancialYearId}
            disabled={fyLoading || financialYears.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder={fyLoading ? 'Loading…' : 'Select financial year'} />
            </SelectTrigger>
            <SelectContent>
              {financialYears.map((y) => (
                <SelectItem key={y.id} value={String(y.id)}>
                  {y.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentYear && (
            <p className="text-xs text-muted-foreground">
              {currentYear.start_date} — {currentYear.end_date}
            </p>
          )}
        </div>
      </div>

      {pageLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading reports…
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Inquiries"
              value={inquiries.count ?? 0}
              icon={Mail}
              color="bg-violet-600"
            />
            <StatCard
              title="School Joins"
              value={joins.count ?? 0}
              icon={Building2}
              color="bg-teal-600"
            />
            <StatCard
              title="Join Revenue"
              value={joins.total_revenue != null ? `₹${Number(joins.total_revenue).toLocaleString()}` : '—'}
              icon={IndianRupee}
              color="bg-emerald-600"
            />
            <StatCard
              title="Salaries (paid)"
              value={salaries.total_paid != null ? `₹${Number(salaries.total_paid).toLocaleString()}` : '—'}
              icon={IndianRupee}
              color="bg-amber-600"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardHeader><CardTitle className="text-base">Inquiries by Month</CardTitle></CardHeader>
              <CardContent className="h-64">
                {inquiryChartData.length > 0 && inquiryChartData.some((d) => d.count > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={inquiryChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#8b5cf6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">{EMPTY_MSG}</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader><CardTitle className="text-base">School Joins by Month</CardTitle></CardHeader>
              <CardContent className="h-64">
                {joinChartData.length > 0 && joinChartData.some((d) => d.count > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={joinChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">{EMPTY_MSG}</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <PreviewTable
              title="Inquiries Preview"
              rows={previews.inquiries}
              rowKey={(r) => r.id}
              columns={[
                { key: 'school', label: 'School', render: (r) => r.school_name || '—' },
                { key: 'date', label: 'Date', render: (r) => r.inquiry_date || '—' },
                { key: 'status', label: 'Status', render: (r) => r.status || '—' },
              ]}
            />
            <PreviewTable
              title="School Joins Preview"
              rows={previews.joins}
              rowKey={(r) => r.id}
              columns={[
                { key: 'school', label: 'School', render: (r) => r.school_name || '—' },
                { key: 'date', label: 'Joined', render: (r) => r.joining_date || '—' },
                { key: 'amount', label: 'Amount', render: (r) => (r.amount != null ? `₹${Number(r.amount).toLocaleString()}` : '—') },
              ]}
            />
            <PreviewTable
              title="Salaries Preview"
              rows={previews.salaries}
              rowKey={(r) => r.id}
              columns={[
                { key: 'employee', label: 'Employee', render: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.employee_code || '—' },
                { key: 'period', label: 'Period', render: (r) => (r.period_start && r.period_end ? `${r.period_start} – ${r.period_end}` : r.month || '—') },
                { key: 'release', label: 'Release', render: (r) => r.release_date || '—' },
                { key: 'status', label: 'Status', render: (r) => r.payment_status || '—' },
                { key: 'salary', label: 'Final', render: (r) => (r.final_salary != null ? `₹${Number(r.final_salary).toLocaleString()}` : '—') },
              ]}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
            {REPORTS.filter((r) => !r.superAdminOnly || isSuperAdmin).map((r) => (
              <Card key={r.key}>
                <CardHeader><CardTitle className="text-base">{r.label}</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    disabled={downloading === `${r.key}-xlsx` || summaryLoading}
                    onClick={() => download(r.key, 'xlsx')}
                  >
                    {downloading === `${r.key}-xlsx` ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    disabled={downloading === `${r.key}-pdf` || summaryLoading}
                    onClick={() => download(r.key, 'pdf')}
                  >
                    {downloading === `${r.key}-pdf` ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {isSuperAdmin && <CrmAttendanceSettings />}
        </>
      )}
    </div>
  );
}
